import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');

    this.#setupFilterHoverEffect();
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;
    this.#animateLayoutChange(target.value);
  }

  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;
    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };

  #setupFilterHoverEffect() {
    const container = this;
    const productCardsSelector = '.collection-product-card';
    const filterTagSelector = '.custom-filters__tag:not(#desktop-more-trigger)';
    const dropdownItemSelector = '.custom-filters__more-dropdown .custom-filters__dropdown-item';
    const moreTrigger = container.querySelector('#desktop-more-trigger');
    const moreDropdown = container.querySelector('#desktop-more-dropdown');
    const defaultCardBg = '#F6F5F4';
    const ALL_HOVER_COLOR = '#DFDFDF';

    const productCards = container.querySelectorAll(productCardsSelector);
    const filterTags = container.querySelectorAll(filterTagSelector);
    const dropdownItems = container.querySelectorAll(dropdownItemSelector);

    const currentPath = window.location.pathname.replace(/\/$/, '');

    filterTags.forEach(tag => tag.classList.remove('custom-filters__tag--active'));
    dropdownItems.forEach(item => item.classList.remove('custom-filters__dropdown-item--active'));
    if (moreTrigger) moreTrigger.classList.remove('custom-filters__tag--active');

    let activeTag = [...filterTags].find(tag => {
      if (!(tag instanceof HTMLAnchorElement)) return false;
      const tagPath = new URL(tag.href, window.location.origin).pathname.replace(/\/$/, '');
      return tagPath === currentPath;
    });

    let activeDropdownItem = null;
    if (!activeTag && dropdownItems.length > 0) {
      activeDropdownItem = [...dropdownItems].find(item => {
        if (!(item instanceof HTMLAnchorElement)) return false;
        const itemPath = new URL(item.href, window.location.origin).pathname.replace(/\/$/, '');
        return itemPath === currentPath;
      });
    }

    if (activeTag) {
      activeTag.classList.add('custom-filters__tag--active');
    } else if (activeDropdownItem) {
      activeDropdownItem.classList.add('custom-filters__dropdown-item--active');
      if (moreTrigger) {
        moreTrigger.classList.add('custom-filters__tag--active');
        moreTrigger.setAttribute('aria-expanded', 'false');
        const activeColor = activeDropdownItem.dataset.color;
        const activeTitle = activeDropdownItem.dataset.filterTitle || activeDropdownItem.querySelector('.custom-filters__dropdown-text')?.textContent.trim();
        if (activeColor) {
          moreTrigger.style.backgroundColor = activeColor;
        }
        if (activeTitle) {
          const textSpan = moreTrigger.querySelector('span');
          if (textSpan) textSpan.textContent = activeTitle;
        }
      }
      if (moreDropdown) {
        moreDropdown.classList.remove('custom-filters__more-dropdown--open');
      }
    }

    productCards.forEach(card => {
      card.addEventListener('mouseenter', () => {
        const currentActiveTag = container.querySelector('.custom-filters__tag--active:not(#desktop-more-trigger)') || container.querySelector('.custom-filters__dropdown-item--active');
        const filterType = currentActiveTag?.dataset.filterType?.toLowerCase();
        const filterTitle = (currentActiveTag?.dataset.filterTitle || currentActiveTag?.textContent || '').trim().toLowerCase();
        const tagColor = currentActiveTag?.dataset.color;

        const isAll = filterType === 'all' || filterTitle === 'all' || tagColor?.toLowerCase() === '#f6f5f4';
        const hoverColor = isAll ? ALL_HOVER_COLOR : (tagColor || ALL_HOVER_COLOR);
        if (hoverColor) card.style.backgroundColor = hoverColor;
      });

      card.addEventListener('mouseleave', () => {
        card.style.backgroundColor = defaultCardBg;
      });
    });
  }
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}